// src/pages/News/SingleNews.tsx

import {
  AccountBalanceTwoTone,
  AddTwoTone,
  AnnouncementTwoTone,
  EditTwoTone,
  MoreVertTwoTone,
} from '@mui/icons-material'
import {
  Avatar,
  Badge,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material'
import { matchSorter } from 'match-sorter'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFilters, useSortBy, useTable } from 'react-table'

import { ContractPool } from '@/types/domain/contract'
import { FolderPool } from '@/types/domain/folder'

import { ASC, DESC, WDDDMMYYYYHHMMSS, userLocales } from '../../api'
import { AM, AMItem, KPage, DefaultColumnFilter } from '../../components'
import {
  OperatorData,
  useContractPool,
  useFolderPool,
  useNewsApi,
  useOperator,
} from '../../hooks'

import {
  contractColumns,
  folderColumns,
  tableRowContract,
  tableRowFolder,
} from './SingleNews.table'

export const SingleNews = () => {
  const navigate = useNavigate()
  const { id: newsId, nc } = useParams()
  const opData: OperatorData = useOperator()
  const fPool: FolderPool = useFolderPool()
  const cPool: ContractPool = useContractPool()

  const [news, setNews] = useState(null)

  const { getById: getNewsById } = useNewsApi()

  useEffect(() => {
    const fetchNews = async () => {
      const data = await getNewsById(Number(newsId))
      setNews(data)
    }

    fetchNews()
  }, [getNewsById, newsId])

  const [actionMenu, setActionMenu] = useState(null)

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable(
      {
        columns: useMemo(
          () => (news?.folder_id === null ? contractColumns : folderColumns),
          [news]
        ),
        data: useMemo(() => {
          if (news?.folder_id === null) {
            return cPool
              .filter(contract =>
                news?.contracts
                  .map(nc => String(nc.entity_id))
                  .includes(contract.id)
              )
              .map(contract => tableRowContract(contract))
          } else {
            return fPool
              .filter(f => Number(f.id) === Number(news?.folder_id))
              .map(f => tableRowFolder(f))
          }
        }, [news, cPool, fPool]),
        defaultColumn: useMemo(() => ({ Filter: DefaultColumnFilter }), []),
        initialState: { sortBy: [{ id: 'name', asc: true }] },
        filterTypes: {
          text: (rows, id, filterValue) =>
            matchSorter(rows, filterValue, { keys: [id] }),
        },
      },
      useFilters,
      useSortBy
    )

  return (
    <>
      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={'News'}
        onClose={() => setActionMenu(null)}
        open={Boolean(actionMenu)}
        historyUrlTarget={'news'}
      >
        <AMItem
          caption={'Neu'}
          icon={<AddTwoTone />}
          onClick={() => navigate('/news/new')}
        />

        <AMItem
          disabled={!opData?.isAdmin}
          caption={'Bearbeiten'}
          icon={<EditTwoTone />}
          onClick={() => navigate(`/news/${newsId}/edit`)}
        />
      </AM>
      {/* action menu end */}

      <KPage
        action={
          <IconButton onClick={event => setActionMenu(event.currentTarget)}>
            <MoreVertTwoTone />
          </IconButton>
        }
        avatar={
          <Avatar>
            <AnnouncementTwoTone />
          </Avatar>
        }
        title={`News: ${news?.title}`}
      >
        <dl>
          <dt>ID</dt>
          <dd>{newsId}</dd>

          <dt>Autor</dt>
          <dd>
            {nc === '1' ? news?.headline : news?.author ? news.author : '-'}
          </dd>

          <dt>Datum</dt>
          <dd>
            {new Date(news?.created_at).toLocaleString(
              userLocales,
              WDDDMMYYYYHHMMSS
            )}
          </dd>

          <dt>Inhalt</dt>
          <dd>
            <div dangerouslySetInnerHTML={{ __html: news?.content_text }} />
          </dd>
        </dl>

        {news?.folder_id !== null && (
          <>
            <Badge
              badgeContent={
                fPool?.filter(f => Number(f.id) === Number(news?.folder_id))
                  .length
              }
              color={'primary'}
              sx={{ mr: 2 }}
            >
              <AccountBalanceTwoTone />
            </Badge>
            Gebäude
          </>
        )}

        {news?.folder_id === null && (
          <>
            <Badge
              badgeContent={news?.contracts?.length}
              color={'primary'}
              sx={{ mr: 2 }}
            >
              <AccountBalanceTwoTone />
            </Badge>
            Verträge
          </>
        )}

        <TableContainer>
          <Table sx={{ width: '100%' }} {...getTableProps()}>
            <TableHead>
              {headerGroups.map(headerGroup => (
                <TableRow
                  key={headerGroup.id}
                  {...headerGroup.getHeaderGroupProps()}
                >
                  {headerGroup.headers.map(column => (
                    <TableCell
                      key={column.id}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      {column.render('Header')}
                      <TableSortLabel
                        active={column.isSorted}
                        direction={column.isSortedDesc ? DESC : ASC}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>

            <TableBody {...getTableBodyProps()}>
              {rows.map(row => {
                prepareRow(row)

                const onClick = () => {
                  const urlChunk =
                    news?.folder_id === null ? 'contracts' : 'folder'
                  const id = row.original.id

                  navigate(`/${urlChunk}/${id}`)
                }

                return (
                  <TableRow
                    key={row.id}
                    onClick={onClick}
                    sx={{ cursor: 'pointer' }}
                    {...row.getRowProps()}
                  >
                    {row.cells.map(cell => (
                      <TableCell
                        key={`${row.id}-${cell.column.id}`}
                        {...cell.getCellProps()}
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </KPage>
    </>
  )
}
